import type { DataGridRowSelectionState, TableFilterState, TableSortingState } from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DownloadTemplateSelection } from '@/entities/download-template';
import {
  type ConversationPipeline,
  type ExpertReportPipelineScope,
  type TranscriptArchiveProgress,
  useDeleteConversations,
  useRunConversationsPipeline,
} from '@/entities/search';
import { fetchAllPages, getApiErrorMessage } from '@/shared/api';
import { getSelectedRowIds, toRowSelection, toastError, toastSuccess } from '@/shared/lib';
import { MASS_RUN_SEARCH_PAGE_SIZE, MASS_RUN_SELECT_ALL_LIMIT } from './constants';
import type { DialogFetchData } from './types';
import { useMassTranscriptArchive } from './useMassTranscriptArchive';

/** Параметры режима массовых действий над списком диалогов */
type Params = {
  /** Массовый запуск обработки доступен только под разрешением `ADMIN_CONVERSATION_PIPELINE` */
  canRunPipeline: boolean;
  /** Удаление доступно только под разрешением `ADMIN_CONVERSATION_DELETE` */
  canDeleteConversations: boolean;
  /** Скачивание транскрипций архивом доступно только под разрешением `DOWNLOAD_TRANSCRIPT_ARCHIVE` */
  canDownloadTranscriptArchive: boolean;
  /**
   * Состав отмеченных строк ведёт сам раздел. Тогда отметки не сбрасываются сменой состава списка
   * и остаются тем же набором, который раздел сохраняет в выборку.
   */
  selectionOverride?: {
    rowSelection: DataGridRowSelectionState;
    onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  };
  /** Ленивая загрузка среза выборки — тем же путём, что и список */
  fetchData: DialogFetchData;
  /** Действующие фильтры выборки (с наложенным слоем перехода) */
  filters: TableFilterState;
  /** Действующая сортировка выборки */
  sorting: TableSortingState;
  /** Ключ состава списка: его смена обесценивает ранее сделанный выбор строк */
  listCompositionKey: string;
  /** Диалоги удалены: показанные строки и открытая запись могут им больше не соответствовать */
  onConversationsDeleted: (conversationIds: number[]) => void;
};

/** Состояние и обработчики режима массовых действий */
type Result = {
  /** Кнопка «Действия» доступна: пользователю разрешено хотя бы одно действие блока */
  canOpenActions: boolean;
  /** Режим массовых действий раскрыт: кнопка «Действия» доступна и нажата */
  isMassActionsActive: boolean;
  /** Единый состав отмеченных строк (поимённый выбор и «выбрать всё») */
  rowSelection: DataGridRowSelectionState;
  /** Число отмеченных диалогов */
  selectedCount: number;
  /** Отмечена хотя бы одна строка — есть над чем выполнять действие */
  hasSelection: boolean;
  /** Идёт постраничный набор id для «выбрать всё» */
  isPreparingSelection: boolean;
  /** Запуск обработки сейчас не запускается: он уже идёт, идёт удаление или набирается «выбрать всё» */
  isRunPipelineLocked: boolean;
  /** Удаление сейчас не запускается: идёт любое действие над отмеченными или набирается «выбрать всё» */
  isDeleteLocked: boolean;
  /** Архив не запускается из-за другого действия: идёт удаление или набирается «выбрать всё» */
  isTranscriptArchiveLocked: boolean;
  /** Идёт запрос удаления */
  isDeletePending: boolean;
  /** Архив транскрипций формируется: от запуска до скачивания или отказа */
  isTranscriptArchivePending: boolean;
  /** Ход формирования архива транскрипций; `null`, пока формирование не запущено */
  transcriptArchiveProgress: TranscriptArchiveProgress | null;
  onToggleMassActions: () => void;
  onRowSelectionChange: (next: DataGridRowSelectionState) => void;
  onRunPipeline: (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => void;
  /** Удаление отмеченных диалогов; завершается и при отказе сервера */
  onDeleteConversations: () => Promise<void>;
  onDownloadTranscriptArchive: (selection: DownloadTemplateSelection) => void;
};

/**
 * Режим массовых действий над списком диалогов: раскрытие блока действий, единый состав отмеченных
 * строк (поимённый выбор и «выбрать всё» в пределах предела массовых действий) и сами действия над
 * отмеченными диалогами — запуск обработки, удаление и скачивание транскрипций архивом.
 *
 * «Выбрать всё» материализует id первых `MASS_RUN_SELECT_ALL_LIMIT` диалогов текущей выборки в тот
 * же `rowSelection`, поэтому действие всегда уходит списком id как есть. Смена состава списка снимает
 * весь выбор: отмеченные id больше не соответствуют показанной выборке.
 */
export const useMassActions = ({
  canRunPipeline,
  canDeleteConversations,
  canDownloadTranscriptArchive,
  selectionOverride,
  fetchData,
  filters,
  sorting,
  listCompositionKey,
  onConversationsDeleted,
}: Params): Result => {
  const { t } = useTranslation(['dialogs']);
  const { mutate: runPipeline, isPending: isRunPipelinePending } = useRunConversationsPipeline();
  const { mutateAsync: deleteConversations, isPending: isDeletePending } = useDeleteConversations();

  // Режим массовых действий раскрывается кнопкой «Действия»: только в нём видны столбец выбора
  // в строках и блок действий под строкой поиска.
  const [isMassActionsOpen, setIsMassActionsOpen] = useState(false);

  // Массовый выбор диалогов: поимённый выбор строк и «выбрать всё» хранятся единым составом
  // отмеченных id — «выбрать всё» материализует id первых диалогов выборки в тот же rowSelection.
  const [ownRowSelection, setOwnRowSelection] = useState<DataGridRowSelectionState>({});
  const rowSelection = selectionOverride?.rowSelection ?? ownRowSelection;
  const setRowSelection = selectionOverride?.onRowSelectionChange ?? setOwnRowSelection;
  // Идёт набор id для «выбрать всё» — блок действий занят до его завершения.
  const [isPreparingSelection, setIsPreparingSelection] = useState(false);
  // Актуальный состав выбора для обработчика мастер-чекбокса: сравнение до/после без устаревшего замыкания.
  const rowSelectionRef = useRef(rowSelection);
  rowSelectionRef.current = rowSelection;
  // Свой состав отметок обесценивается сменой состава списка, чужой — нет: раздел ведёт его сам.
  const isOwnSelection = selectionOverride == null;

  // Отмеченные диалоги — единый состав для поимённого выбора и «выбрать всё».
  const selectedConversationIds = useMemo(() => getSelectedRowIds(rowSelection), [rowSelection]);

  const canOpenActions = canRunPipeline || canDeleteConversations || canDownloadTranscriptArchive;
  const isMassActionsActive = canOpenActions && isMassActionsOpen;
  const hasSelection = selectedConversationIds.length > 0;
  // Пока набирается «выбрать всё», состав отметок неполон, а пока идёт удаление, отмеченные диалоги
  // уходят: ни одно действие над отмеченными в это время не запускается.
  const isSelectionUnsettled = isPreparingSelection || isDeletePending;

  const { isTranscriptArchivePending, transcriptArchiveProgress, onDownloadTranscriptArchive } =
    useMassTranscriptArchive({ conversationIds: selectedConversationIds, isLocked: isSelectionUnsettled });

  // Запуск обработки и архив отмеченные диалоги только читают и друг другу не мешают; повторно
  // каждое не запускается, пока идёт само.
  const isRunPipelineLocked = isSelectionUnsettled || isRunPipelinePending;
  // Удаление уносит сами диалоги, поэтому не запускается поверх любого идущего действия: обработка
  // пошла бы по удалённым диалогам, а формирующийся архив лишился бы их транскрипций.
  const isDeleteLocked = isRunPipelineLocked || isTranscriptArchivePending;

  // Смена состава списка (фильтры/поиск/сортировка) обесценивает ранее сделанный выбор строк
  // и режим «выбрать все»: выбранные id больше не соответствуют показанной выборке. Состав,
  // который ведёт сам раздел, это не касается — там отметки набирают разными отборами.
  useEffect(() => {
    if (isOwnSelection) setOwnRowSelection({});
  }, [listCompositionKey, isOwnSelection]);

  // Кнопка «Действия»: раскрывает/сворачивает режим; сворачивание снимает свой массовый выбор.
  const handleToggleMassActions = useCallback(() => {
    setIsMassActionsOpen((open) => {
      if (open && isOwnSelection) setOwnRowSelection({});
      return !open;
    });
  }, [isOwnSelection]);

  // «Выбрать всё»: набираем id первых MASS_RUN_SELECT_ALL_LIMIT диалогов текущей выборки (тем же
  // срезом, что и список: действующие поиск/фильтры/сортировка) и отмечаем их как выбранные. Поиск
  // отдаёт не более страницы за запрос, поэтому предел набирается постранично.
  const selectAllUpToLimit = useCallback(async () => {
    setIsPreparingSelection(true);
    try {
      const { items } = await fetchAllPages(
        (offset, limit) => fetchData({ offset, limit, filters, sorting }),
        MASS_RUN_SEARCH_PAGE_SIZE,
        MASS_RUN_SELECT_ALL_LIMIT,
      );
      setRowSelection(toRowSelection(items.map((item) => item.id)));
    } catch {
      toastError(t('dialogs:dialogListSection.massActions.selectAllError'));
    } finally {
      setIsPreparingSelection(false);
    }
  }, [fetchData, filters, sorting, setRowSelection, t]);

  // Изменение мульти-выбора DataGrid. Мастер-чекбокс кита выделяет разом все строки во вьюпорте
  // (>1 строки за жест) — такой массовый жест трактуем как «выбрать всю текущую выборку в пределах
  // предела», массовое снятие — как очистку. Изменение ровно одной строки — поимённый выбор.
  const handleRowSelectionChange = useCallback(
    (next: DataGridRowSelectionState) => {
      if (isPreparingSelection) return;
      const prev = rowSelectionRef.current;
      const addedCount = Object.keys(next).filter((id) => next[id] && !prev[id]).length;
      const removedCount = Object.keys(prev).filter((id) => prev[id] && !next[id]).length;
      if (addedCount > 1) {
        // Мастер-чекбокс «выбрать всё»: сразу собираем всю выборку до предела и выставляем итоговый
        // состав разом, не отмечая промежуточно только видимый вьюпорт. Иначе счётчик мигает числом
        // видимых строк (например 25), а через пару секунд — итоговым (500/N). На время сбора блок
        // действий показывает индикатор ожидания.
        void selectAllUpToLimit();
        return;
      }
      if (removedCount > 1) {
        setRowSelection({});
        return;
      }
      setRowSelection(next);
    },
    [isPreparingSelection, selectAllUpToLimit, setRowSelection],
  );

  // Исход состоявшегося массового действия, общий у всех: оно названо пользователю, а свой выбор
  // снимается — отмечать им больше нечего. Состав, который ведёт раздел, действие не трогает:
  // это набор записей выборки, а не разовая отметка под действие.
  const completeMassAction = useCallback(
    (message: string) => {
      toastSuccess(message);
      if (isOwnSelection) setOwnRowSelection({});
    },
    [isOwnSelection],
  );

  // Массовый запуск обработки по отмеченным диалогам. Выборка уже материализована составом id
  // (поимённо либо «выбрать всё» в пределах предела), поэтому уходит списком id как есть.
  // Область запуска задаётся только у генерации заключений; без неё запуск прежний — целиком.
  const handleRunPipeline = useCallback(
    (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => {
      if (!hasSelection || isRunPipelineLocked) return;
      runPipeline(
        { filters: { conversationIds: selectedConversationIds }, pipeline, ...scope },
        {
          onSuccess: (result) => {
            // Усечение выборки бэком по своему пределу подтверждение отмечает.
            completeMassAction(
              result.truncated
                ? t('dialogs:dialogListSection.runPipeline.successTruncated', {
                    count: result.accepted,
                    limit: MASS_RUN_SELECT_ALL_LIMIT,
                  })
                : t('dialogs:dialogListSection.runPipeline.success', { count: result.accepted }),
            );
          },
        },
      );
    },
    [hasSelection, isRunPipelineLocked, selectedConversationIds, runPipeline, completeMassAction, t],
  );

  // Удаление отмеченных диалогов тем же составом id, что и запуск обработки. Об исходе — и об
  // удалении, и об отказе — сообщает само действие, называя его: причина отказа приходит телом
  // ответа. Промис не отклоняется: подтверждение закрывается и при отказе, а отметки тогда
  // остаются, чтобы удаление можно было повторить.
  const handleDeleteConversations = useCallback(async () => {
    if (!hasSelection || isDeleteLocked) return;
    const result = await deleteConversations({ filters: { conversationIds: selectedConversationIds } }).catch(
      (error: unknown) => {
        toastError(t('dialogs:dialogListSection.deleteDialogs.error'), { description: getApiErrorMessage(error) });
        return null;
      },
    );
    if (!result) return;
    completeMassAction(t('dialogs:dialogListSection.deleteDialogs.success', { count: result.deleted }));
    onConversationsDeleted(result.conversationIds);
  }, [
    hasSelection,
    isDeleteLocked,
    selectedConversationIds,
    deleteConversations,
    completeMassAction,
    onConversationsDeleted,
    t,
  ]);

  return {
    canOpenActions,
    isMassActionsActive,
    rowSelection,
    selectedCount: selectedConversationIds.length,
    hasSelection,
    isPreparingSelection,
    isRunPipelineLocked,
    isDeleteLocked,
    isTranscriptArchiveLocked: isSelectionUnsettled,
    isDeletePending,
    isTranscriptArchivePending,
    transcriptArchiveProgress,
    onToggleMassActions: handleToggleMassActions,
    onRowSelectionChange: handleRowSelectionChange,
    onRunPipeline: handleRunPipeline,
    onDeleteConversations: handleDeleteConversations,
    onDownloadTranscriptArchive,
  };
};
